import axios from 'axios';
import { useCreatorStudioStore } from '../stores/CreatorStudioStore';
import Job from './../model/Job';
import { EVENT_CREDITS_UPDATE } from '../events/credits.events';
import { EVENT_JOB_FAIL, EVENT_JOB_START } from '../events/job.events';
import {
    JOB_STATE_COMPLETE,
    JOB_STATE_DRAFT,
    JOB_STATE_FAILED,
    JOB_STATE_INCOMPLETE,
    JOB_STATE_PROCESSING,
} from '../const/job.const';
import { EventBus } from '../events/event.bus';
import ApiManager from './ApiManager';
import FileManager from './FileManager';
import ImagesManager from './ImagesManager';
import { EVENT_APP_NAVIGATE } from '../events/app.events';
import { decodeBase64ToImageUrl, encodeImageToBase64 } from '../../../../../helper/base64';
import { satReconvert } from '../../../../../../lib-sat/result/util/reconvert';
import { useUserStore } from '../../../../../../lib-sat/store/modules/user-store';
import { satGlobals } from '../../../../../sat/helper/sat-globals';
import Conversion from '../model/Conversion';

class JobManager {
    listener = false;

    constructor() {}

    create() {
        this.store = useCreatorStudioStore();
        this.userStore = useUserStore();

        const job = new Job();
        job.isOpen = true;
        job.settings.prompt = '';
        job.settings.negative_prompt = '';

        if (this.userStore.user?.isPremium()) {
            const { batch_count, batch_size } = ImagesManager.getByNumber(4);

            job.settings.conversion.options.batch_count = batch_count;
            job.settings.conversion.options.batch_size = batch_size;
        }

        this.store.addJob(job);

        return job.id;
    }

    async submit(id, operation = 'aicreatorstudio') {
        this.store = useCreatorStudioStore();
        let job = this.store.getJobById(id);

        let finalPrompt = '';
        let negativePrompt = '';

        let negativePromptField = this.store.getSettings().negative_prompt;

        if (negativePromptField !== '') {
            negativePrompt += negativePromptField;
        }

        let promptField = this.store.getSettings().prompt;
        if (promptField !== '') {
            if (finalPrompt !== '') {
                finalPrompt += ',';
            }
            finalPrompt += promptField;
        }

        let similiarImagePrompt = job.settings.similiarImageJob.prompt;

        if (similiarImagePrompt !== '') {
            if (finalPrompt !== '') {
                finalPrompt += ',';
            }
            finalPrompt += similiarImagePrompt;
        }

        if (this.store.getSettings().prompt_modifier.length > 0) {
            let modifier = this.store.getSettings().prompt_modifier.join(',');
            if (finalPrompt !== '') {
                finalPrompt += ',';
            }
            finalPrompt += modifier;
        }

        let apiJob = await ApiManager.create({operation: operation});

        if (null === apiJob) {
            job.state = JOB_STATE_FAILED;
            return;
        }

        job.apiJob = apiJob;


        let prompt = FileManager.fromText('prompt.txt', 'prompt', finalPrompt);

        await ApiManager.uploadBase64(apiJob, prompt);

        if (negativePrompt !== '') {
            let negativePromptText = FileManager.fromText(
                'negative_prompt.txt',
                'negative_prompt',
                negativePrompt
            );
            await ApiManager.uploadBase64(apiJob, negativePromptText);
        }

        let conversion = job.settings.conversion;

        let conversionStart = await ApiManager.start(apiJob, conversion);

        if (null === conversionStart) {
            job.state = JOB_STATE_FAILED;
            return;
        }

        await this._waitUntilCompleteJob(id, apiJob);
    }

    async submitSimiliarImage(id) {
        this.store = useCreatorStudioStore();
        let job = this.store.getJobById(id);

        let apiJob = await ApiManager.create({ operation: 'system analyzeimage' });

        if (null === apiJob) {
            job.state = JOB_STATE_FAILED;
            return;
        }

        job.settings.similiarImageJob.analyzeJob = apiJob;

        await ApiManager.addInputs(apiJob, {
            type: 'remote',
            source: job.settings.similiarImageJob.apiJob.output[0].uri,
        });

        let conversion = {
            target: 'analyze-image',
            category: 'operation',
            options: {
                prompt: true,
            },
        };

        let conversionStart = await ApiManager.start(apiJob, conversion);

        if (null === conversionStart) {
            job.state = JOB_STATE_FAILED;
            return;
        }

        await this._waitUntilComplete(apiJob);
        let finalJob = await ApiManager.get(apiJob);

        job.settings.similiarImageJob.analyzeJob = finalJob;

        let promptData = await axios.get(finalJob.output[0].uri);

        job.settings.similiarImageJob.prompt = promptData.data.prompt;
        this.submit(id, 'aisimilarimage');
    }

    async submitFromJobId(id, apiId) {
        this.store = useCreatorStudioStore();
        
        let job = this.store.getJobById(id);
        job.state = JOB_STATE_PROCESSING;

        let apiJob = await ApiManager.get({ id: apiId });

        if (null === apiJob) {
            job.state = JOB_STATE_DRAFT;
            this.store.updateSettings(JSON.parse(JSON.stringify(this.store.getJobById(id).settings)));
            EventBus.$emit(EVENT_APP_NAVIGATE, `/${id}/welcome`, {});

            return;
        }

        job.apiJob = apiJob;

        let conversion = new Conversion();

        if (job.apiJob.conversion[0]) {
            conversion.options.width = job.apiJob.conversion[0].options.width;
            conversion.options.height = job.apiJob.conversion[0].options.height;
            conversion.options.style = job.apiJob.conversion[0].options.style;
        }

        if (this.userStore.user?.isPremium()) {
            const {batch_count, batch_size} = ImagesManager.getByNumber(4);

            conversion.options.batch_count = batch_count;
            conversion.options.batch_size = batch_size;
        }

        job.settings.conversion = conversion;

        if (!job.apiJob.conversion[0]) {
            job.settings.activeTool = 'similiarimage';
            
            job.settings.similiarImageJob.apiJob = {
                ...job.apiJob,
                status: {
                    code: 'incomplete'
                }
            };

            this.store.updateSettings(JSON.parse(JSON.stringify(this.store.getJobById(id).settings)));
            
            EventBus.$emit(EVENT_APP_NAVIGATE, `/${id}/canvas`, {});

            await this._generateThumbnailFromApiJob(apiJob, job);
            
            this.store.updateSettings(JSON.parse(JSON.stringify(this.store.getJobById(id).settings)));

            this.submitSimiliarImage(id);
            
            return;
        }
        
        

        const extraInfo = apiJob?.sat?.job_extra;
        job.settings.prompt = extraInfo?.prompt;
        job.settings.negative_prompt = extraInfo?.negative_prompt;

        this.store.updateSettings(JSON.parse(JSON.stringify(this.store.getJobById(id).settings)));

        EventBus.$emit(EVENT_APP_NAVIGATE, `/${id}/canvas`, {});

        await this._waitUntilCompleteJob(id, apiJob);
    }

    async _generateThumbnailFromApiJob(apiJob, job) {
        await ApiManager.start(apiJob, {
            target: 'jpg',
            category: 'image',
            options: {
                'width': 1100
            },
        }).catch((e) => {
            return;
        });
        await this._waitUntilComplete(apiJob);

        let finalJob = await ApiManager.get(job.apiJob);
        job.settings.similiarImageJob.apiJob = finalJob;

        let base64Image = await this._getBase64Image(finalJob.output[0].uri);
        job.settings.similiarImageJob.base64 = base64Image;
    }

    async _getBase64Image(url) {
        return new Promise((resolve, reject) => {
            let uri = url;
            encodeImageToBase64(uri).then((base64String) => {
                resolve(decodeBase64ToImageUrl(base64String));
            });
        });
    }

    async _waitUntilComplete(job, incomplete = 0) {
        return new Promise((resolve, reject) => {
            async function _waitUntilComplete(job, incomplete = 0) {
                let apiJob = await ApiManager.get(job);
                if (null === apiJob) {
                    resolve();

                    return;
                }

                if (apiJob.status.code === JOB_STATE_FAILED) {
                    EventBus.$emit(EVENT_JOB_FAIL, {});

                    resolve();
                    return;
                }

                if (apiJob.status.code === JOB_STATE_COMPLETE) {
                    resolve();
                    return;
                }

                if (apiJob.status.code === JOB_STATE_INCOMPLETE) {
                    if (incomplete > 60) {
                        job.state = JOB_STATE_FAILED;

                        resolve();
                        return;
                    }

                    setTimeout(() => {
                        _waitUntilComplete(job, ++incomplete);
                    }, 2100);
                    return;
                }

                setTimeout(() => {
                    _waitUntilComplete(job);
                }, 2100);

                return;
            }

            _waitUntilComplete(job, incomplete);
        });
    }

    _populateBase64Images(id) {
        this.store = useCreatorStudioStore();

        let job = this.store.getJobById(id);

        async function processOutputs() {
            for (let i = 0; i < job.apiJob.output.length; i++) {
                const output = job.apiJob.output[i];
                try {
                    const base64String = await encodeImageToBase64(output.uri);
                    if (base64String === null) {
                        job.state = JOB_STATE_FAILED;
                        break; // Exit loop if encoding fails
                    }
                    job.base64_output_images.push(decodeBase64ToImageUrl(base64String));
                } catch (err) {
                    job.state = JOB_STATE_FAILED;
                    break; // Exit loop if encoding throws an error
                }
            }
            // Emit event if job state is failed
            if (job.state === JOB_STATE_FAILED) {
                EventBus.$emit(EVENT_JOB_FAIL, {});
            }
        }
        
        // Call the function to start processing outputs
        processOutputs();
        
    }

    listen() {
        if (this.listener === true) {
            return;
        }

        this.listener = true;

        EventBus.$on(EVENT_JOB_START, (data) => {
            this.start(data.id);
        });
    }

    start(id) {
        this.store = useCreatorStudioStore();

        let job = this.store.getJobById(id);

        if (job.state !== JOB_STATE_DRAFT && job.state !== JOB_STATE_FAILED) {
            id = this.create();
            job = this.store.getJobById(id);
        }

        job.settings = JSON.parse(JSON.stringify(this.store.getSettings()));

        job.state = JOB_STATE_PROCESSING;
        EventBus.$emit(EVENT_APP_NAVIGATE, `/${id}/canvas`, {});
        if (job.settings.activeTool === 'texttoimage') {
            this.submit(id);
        }

        if (job.settings.activeTool === 'similiarimage') {
            this.submitSimiliarImage(id);
        }
    }

    export(id) {
        this.store = useCreatorStudioStore();

        let locale = satGlobals.getLocale();
        let job = this.store.getJobById(id);

        if (locale !== 'en') {
            window.open('/' + locale + '/result#j=' + job.apiJob.id);
        } else {
            window.open('/result#j=' + job.apiJob.id);
        }
    }

    openOperation(id, operation) {
        this.store = useCreatorStudioStore();
        let index = 0;
        const job = this.store.getJobById(id);
        const jobSettings = this.store.getSettings();

        if (jobSettings.image_gallery_index && jobSettings.image_gallery_index > 0) {
            index = jobSettings.image_gallery_index;
        }

        let operationLink = job.apiJob.sat_links.reconvert.filter((obj) => {
            return obj.operation === operation;
        });
        satReconvert.redirectWithStorage(operationLink[0].href.link, job.apiJob.output[index].uri);
    }

    async _waitUntilCompleteJob(id, job, incomplete = 0) {
        this.store = useCreatorStudioStore();

        let apiJob = await ApiManager.get(job);

        if (null === apiJob) {
            this.store.getJobById(id).state = JOB_STATE_FAILED;
            return;
        }

        if (!this.store.getJobById(id)) {
            return;
        }

        this.store.getJobById(id).apiJob = apiJob;

        if (apiJob.status.code === JOB_STATE_FAILED) {
            this.store.getJobById(id).state = JOB_STATE_FAILED;
            EventBus.$emit(EVENT_JOB_FAIL, {});

            return;
        }

        if (apiJob.status.code === JOB_STATE_COMPLETE) {
            this.store.getJobById(id).state = this.store.getJobById(id).apiJob.status.code;
            this._populateBase64Images(id);
            EventBus.$emit(EVENT_CREDITS_UPDATE, {});
            return;
        }

        if (apiJob.status.code === JOB_STATE_INCOMPLETE) {
            if (incomplete > 60) {
                job.state = JOB_STATE_FAILED;
                this.store.getJobById(id).state = JOB_STATE_FAILED;

                return;
            }

            setTimeout(() => {
                this._waitUntilCompleteJob(id, job, ++incomplete);
            }, 500);

            return;
        }

        setTimeout(() => {
            this._waitUntilCompleteJob(id, job);
        }, 500);
    }
}

export default new JobManager();
